import React, { useEffect, useMemo, useState } from "react";

import SortableTable, { TSortableColumn } from "../../common/SortableTable";

import { useAppSelector } from "../../../../store/hooks";
import {
  selectAverageData,
  selectProfitCalculator,
} from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { formatCurrency, formatNumber, toNumber } from "../../../../helpers";
import { Box, useMediaQuery } from "@mui/material";
import Calculator from "../../../../helpers/profitCalculator";
// import { selectIsFreePlan } from "../../../../features/pricingPlans/pricingPlans.selector";
import useProductAnalyzerParams from "../../../../hooks/useProductAnalyzerParams";

interface IStatisticRow {
  type: string;
  avg30: number;
  avg90: number;
  avg180: number;
  avg365: number;
}

const filterZero = (value: number) => {
  return !value ? "-" : formatCurrency(value);
};

const TABLE_COLUMNS: TSortableColumn<IStatisticRow>[] = [
  {
    header: "Type",
    accessorKey: "type",
    align: "left",
  },
  {
    header: "30 days",
    accessorKey: "avg30",
    formatter: ({ avg30 }) => filterZero(avg30),
  },
  {
    header: "90 days",
    accessorKey: "avg90",
    formatter: ({ avg90 }) => filterZero(avg90),
  },
  {
    header: "180 days",
    accessorKey: "avg180",
    formatter: ({ avg180 }) => filterZero(avg180),
  },
  {
    header: "1 year",
    accessorKey: "avg365",
    formatter: ({ avg365 }) => filterZero(avg365),
  },
];

const ASINStatistics: React.FC = () => {
  const { averages } = useAppSelector(selectAverageData);
  const { profitCalculator, feeRules } = useAppSelector(selectProfitCalculator);
  // const isFreePlan = useAppSelector(selectIsFreePlan);
  const [lowest, setLowest] = useState<string>();
  const [avg, setAvg] = useState<string>();
  const [highest, setHighest] = useState<string>();
  const [lowestValue, setLowestValue] = useState<number | null>(null);
  const [avgValue, setAvgValue] = useState<number | null>(null);
  const [highestValue, setHighestValue] = useState<number | null>(null);

  const { asin, lookupId } = useProductAnalyzerParams();
  const isSmallScreen = useMediaQuery("(max-width:419px)");

  // Calculate profit and ROI for a given sale price
  const calculateProfitAndRoi = (salePriceValue: number) => {
    if (salePriceValue == null || !profitCalculator || !feeRules) {
      return { profit: 0, roi: 0 };
    }
    const calculator = new Calculator(
      {
        ...profitCalculator,
        salePrice: salePriceValue,
      },
      feeRules,
    );
    const { profit, roi } = calculator.calculate();
    return { profit: toNumber(profit), roi: toNumber(roi) };
  };

  // Get badge color based on profit/ROI criteria
  const getBadgeColor = (value: number | null) => {
    if (value == null || !profitCalculator) {
      return {
        borderColor: "#C8C8C8",
        backgroundColor: "#F8F8F8",
        textColor: "#0F1111",
      };
    }
    const numericValue = toNumber(value ?? 0);
    const { profit: calculatedProfit, roi: calculatedRoi } =
      calculateProfitAndRoi(numericValue);
    const minProfit = toNumber(profitCalculator.minProfit);
    const minRoi = toNumber(profitCalculator.minRoi);
    const meetsCriteria =
      calculatedProfit >= minProfit && calculatedRoi >= minRoi;

    return meetsCriteria
      ? {
          borderColor: "#87E8AD",
          backgroundColor: "#E9FBEF",
          textColor: "#047647",
        }
      : {
          borderColor: "#A9444299",
          backgroundColor: "#F2DEDE",
          textColor: "#A94442",
        };
  };

  const statistics: IStatisticRow[] = useMemo(() => {
    if (!averages) return [];
    const data = [];
    data.push({
      type: "FBA ($)",
      ...averages.newFba,
    });
    data.push({
      type: "Amazon ($)",
      ...averages.newAmazon,
    });
    data.push({
      type: "BuyBox ($)",
      ...averages.buyBox,
    });
    data.push({
      type: "New ($)",
      ...averages.new,
    });
    return data;
  }, [averages]);

  useEffect(() => {
    let prices = [];
    if (averages?.newFba) {
      const { avg30, avg90, avg180, avg365 } = averages.newFba;
      prices.push(avg30, avg90, avg180, avg365);
    }
    if (averages?.newAmazon) {
      const { avg30, avg90, avg180, avg365 } = averages.newAmazon;
      prices.push(avg30, avg90, avg180, avg365);
    }
    if (averages?.buyBox) {
      const { avg30, avg90, avg180, avg365 } = averages.buyBox;
      prices.push(avg30, avg90, avg180, avg365);
    }
    if (averages?.new) {
      const { avg30, avg90, avg180, avg365 } = averages.new;
      prices.push(avg30, avg90, avg180, avg365);
    }
    prices = prices.filter((value) => value > 0);
    const lowestNum = prices.length > 0 ? Math.min(...prices) : null;
    const highestNum = prices.length > 0 ? Math.max(...prices) : null;
    const avgNum =
      prices.length > 0
        ? prices.reduce((sum, value) => sum + value, 0) / prices.length
        : null;

    setLowestValue(lowestNum);
    setHighestValue(highestNum);
    setAvgValue(avgNum);
    setLowest(lowestNum != null ? formatCurrency(lowestNum) : "-");
    setHighest(highestNum != null ? formatCurrency(highestNum) : "-");
    setAvg(avgNum != null ? formatCurrency(avgNum) : "-");
  }, [averages]);

  return (
    <Box>
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr 1fr"
        gap="4px"
        paddingY="4px"
        paddingX="4px"
        sx={{ width: "100%", boxSizing: "border-box" }}
      >
        <Box
          sx={{
            borderRadius: "4px",
            backgroundColor: getBadgeColor(lowestValue).backgroundColor,
            border: `1px solid ${getBadgeColor(lowestValue).borderColor}`,
            color: getBadgeColor(lowestValue).textColor,
            px: "8px",
            py: "2px",
            fontSize: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "15px",
            minWidth: 0,
            textAlign: "center",
            flexDirection: isSmallScreen ? "column" : "row",
            // overflow: "hidden",
            // textOverflow: "ellipsis",
            // whiteSpace: "nowrap",
          }}
        >
          Lowest{isSmallScreen ? "" : ":"}&nbsp;
          <span style={{ fontWeight: 700 }}>{lowest ?? "-"}</span>
        </Box>
        <Box
          sx={{
            borderRadius: "4px",
            backgroundColor: getBadgeColor(avgValue).backgroundColor,
            border: `1px solid ${getBadgeColor(avgValue).borderColor}`,
            color: getBadgeColor(avgValue).textColor,
            px: "8px",
            py: "2px",
            fontSize: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "15px",
            minWidth: 0,
            textAlign: "center",
            flexDirection: isSmallScreen ? "column" : "row",
            // overflow: "hidden",
            // textOverflow: "ellipsis",
            // whiteSpace: "nowrap",
          }}
        >
          Average{isSmallScreen ? "" : ":"}&nbsp;
          <span style={{ fontWeight: 700 }}>{avg ?? "-"}</span>
        </Box>
        <Box
          sx={{
            borderRadius: "4px",
            backgroundColor: getBadgeColor(highestValue).backgroundColor,
            border: `1px solid ${getBadgeColor(highestValue).borderColor}`,
            color: getBadgeColor(highestValue).textColor,
            px: "8px",
            py: "2px",
            fontSize: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: "15px",
            minWidth: 0,
            textAlign: "center",
            flexDirection: isSmallScreen ? "column" : "row",
            // overflow: "hidden",
            // textOverflow: "ellipsis",
            // whiteSpace: "nowrap",
          }}
        >
          Highest{isSmallScreen ? "" : ":"}&nbsp;
          <span style={{ fontWeight: 700 }}>{highest ?? "-"}</span>
        </Box>
      </Box>
      <SortableTable
        columns={TABLE_COLUMNS}
        data={statistics || []}
        noBorderBottom
        // noBorderTop
        noFilters
        noSearch
        noDefaultSort
        // lock={isFreePlan}
        asin={asin}
        lookupId={lookupId}
        title="ASIN Statistic"
        // headerBackground="#FFFFFFF7"
      />
    </Box>
  );
};

export default ASINStatistics;
